/**
 * useSatelliteFrames.ts
 * يقرأ فهرس إطارات القمر الصناعي (satellite_meta.json) الذي يولّده الـbackend.
 *
 * يُستهلَك في مكانين: NewWeatherMap يبني منه **محور الشريط الزمني** حين تكون طبقة القمر
 * فعّالة، و SatelliteTileLayer يختار منه النسيج المعروض. لذا يعيش هنا لا داخل الطبقة —
 * جلبه مرّتين يعني محورين قد يختلفان.
 */

import { useEffect, useState } from 'react';

export interface SatelliteFrame {
    /** مجلّد هرم البلاطات لهذا الإطار، نسبةً إلى rasters/ (مثل "sat/1754400000"). */
    dir: string;
    epoch: number;
}

export interface SatelliteMeta {
    generated_epoch: number;
    /** حجم البلاطة ومدى الـzoom الذي بُني به الهرم — تبنيها الواجهة في مصدر raster. */
    tileSize?: number;
    minzoom?: number;
    maxzoom?: number;
    /** النسيج العالمي الواحد داخل كل مجلّد إطار — المسار السلس (المزج على الـGPU). */
    globalName?: string;
    globalWidth?: number;
    /** حدّا العرض اللذان تمتدّ بينهما بيانات الموزاييك — للتوثيق فقط: التلاشي القطبي
     *  مخبوز في قناة alpha للبلاطات، فلا تحتاجهما الواجهة في قناع. */
    lat_north?: number;
    lat_south?: number;
    frames: SatelliteFrame[];
    /** الطبقة عالية الدقّة لمنطقة محدّدة (satellite_hires.py) — اختيارية تماماً. */
    hires?: SatelliteHires;
}

/** رقعة عالية الدقّة واحدة: منطقة ثابتة (الجزيرة العربية) أو مربّع حول عاصفة. */
export interface HiresPatch {
    id: string;
    /** مجلّد البلاطات نسبةً إلى rasters/. */
    dir: string;
    /** [غرب، جنوب، شرق، شمال] — حدود البلاطات الموجودة؛ لا يُطلب شيء خارجها. */
    bounds: [number, number, number, number];
}

/**
 * بلاطات z6-7 لرقع محدّدة فوق الموزاييك العالمي (z0-5). فهرسها ملفّ مستقلّ يولّده
 * مولّد مستقلّ، فغيابه أو تأخّره لا يمسّ الطبقة الأساسية: الإطار بلا رقع يُعرض
 * بالموزاييك وحده كما كان. مربّعات العواصف تتحرّك معها، فالرقع تختلف من إطار لآخر.
 */
export interface SatelliteHires {
    tileSize: number;
    minzoom: number;
    maxzoom: number;
    /** epoch → رقع ذلك الإطار. */
    patches: Record<number, HiresPatch[]>;
}

interface HiresIndex {
    tileSize: number;
    minzoom: number;
    maxzoom: number;
    frames: { epoch: number; patches: HiresPatch[] }[];
}

function toHires(ix: HiresIndex | null): SatelliteHires | undefined {
    if (!ix?.frames?.length) return undefined;
    const patches: Record<number, HiresPatch[]> = {};
    for (const fr of ix.frames) {
        const ok = (fr?.patches ?? []).filter((p) => p?.dir && Array.isArray(p.bounds) && p.bounds.length === 4);
        if (ok.length) patches[fr.epoch] = ok;
    }
    return { tileSize: ix.tileSize ?? 512, minzoom: ix.minzoom, maxzoom: ix.maxzoom, patches };
}

/** دورة تحديث الفهرس — الـbackend يعيد بناء السلسلة كل ~15 دقيقة عبر cron. */
const REFRESH_MS = 15 * 60 * 1000;

export function useSatelliteFrames(enabled: boolean): SatelliteMeta | null {
    const [meta, setMeta] = useState<SatelliteMeta | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        const load = () => {
            // ?v يتغيّر كل دورة تحديث: بدونه يخدم الكاش فهرساً قديماً فلا تظهر أحدث صورة.
            const v = Math.floor(Date.now() / REFRESH_MS);
            // فهرس الدقّة العالية يُجلب بالتوازي ولا يُنتظر فشله: أي خطأ فيه = undefined.
            const hiresP = fetch(`${import.meta.env.BASE_URL}rasters/satellite_hires_meta.json?v=${v}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((ix: HiresIndex | null) => toHires(ix))
                .catch(() => undefined);
            fetch(`${import.meta.env.BASE_URL}rasters/satellite_meta.json?v=${v}`)
                .then((r) => (r.ok ? r.json() : null))
                .then(async (m: SatelliteMeta | null) => (m ? { ...m, hires: await hiresP } : null))
                .then((m: SatelliteMeta | null) => {
                    if (cancelled) return;
                    // نقبل الإطارات التي تحمل مجلّد بلاطات فقط. الفهرس المولَّد بالمعمارية
                    // السابقة (صورة عالمية واحدة لكل إطار) يحمل `file` لا `dir`، ولولا هذا
                    // الفرز لبنت الواجهة روابط `rasters/undefined/{z}/{x}/{y}.webp` فأمطرت
                    // الخادم بمئات الـ404 لكل زائر حتى تُعاد التوليدة. الفهرس القديم يعني
                    // «لا سلسلة» — فتبقى الطبقة فارغة ويبقى الشريط على محور التوقّعات.
                    const frames = m?.frames?.filter((fr) => !!fr?.dir) ?? [];
                    if (frames.length) setMeta({ ...(m as SatelliteMeta), frames });
                })
                .catch(() => { /* السلسلة غير مولَّدة بعد — تبقى الطبقة فارغة */ });
        };
        load();
        const id = window.setInterval(load, REFRESH_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled]);

    return meta;
}
